import React from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Info, X } from "lucide-react";
import { formatDateOnly } from "@/lib/datetime";

interface FilterResult {
  // Patient Demographics
  patient_name?: string;
  patient_phone?: string;
  patient_gender?: string;
  patient_age_min?: number;
  patient_age_max?: number;
  include_tags?: string;
  exclude_tags?: string;

  // Call Status & History
  call_statuses?: string;
  call_types?: string;
  call_date_start?: string;
  call_date_end?: string;
  has_calls?: boolean;

  // Call Outcome Analysis
  latest_call_status?: string;
  call_attempts_min?: number;
  call_attempts_max?: number;
  never_answered?: boolean;

  // Consent Management
  consent_status?: string;
  consent_method?: string;
  needs_consent?: boolean;

  // Time-based Filters
  last_contact_start?: string;
  last_contact_end?: string;
  due_for_contact?: boolean;
  overdue_contact?: boolean;

  // Care Management
  care_plan_status?: string;
  has_active_care_plan?: boolean;
  scheduled_calls_pending?: boolean;

  // Complex Logical Filters
  call_success_rate_min?: number;
  call_success_rate_max?: number;
  response_pattern?: string;
  priority_level?: string;

  // Negative filtering for calls
  no_calls_start?: string;
  no_calls_end?: string;
  no_call_types?: string;
  no_call_types_start?: string;
  no_call_types_end?: string;
}

interface PatientStats {
  patient_count: number;
  avg_success_rate: number;
  consent_pending_count: number;
  overdue_count: number;
}

interface CallAggregations {
  total_calls: number;
  by_status: Record<string, number>;
  by_strategy_type: Record<string, number>;
  avg_duration_seconds: number;
  total_duration_minutes: number;
}

interface SmartSearchActiveBarProps {
  isVisible: boolean;
  query: string | null;
  filters: FilterResult | null;
  patientStats?: PatientStats | null;
  callAggregations?: CallAggregations | null;
  onClear: () => void;
}

const SmartSearchActiveBar: React.FC<SmartSearchActiveBarProps> = ({
  isVisible,
  query,
  filters,
  patientStats,
  callAggregations,
  onClear,
}) => {
  // Helper function to format filter values
  const formatFilterValue = (key: string, value: any): string => {
    if (typeof value === "boolean") {
      return value ? "Yes" : "No";
    }
    if (typeof value === "number") {
      if (key.includes("rate")) {
        return `${(value * 100).toFixed(1)}%`;
      }
      return value.toString();
    }
    if (typeof value === "string") {
      // Handle comma-separated values
      if (value.includes(",")) {
        return value.split(",").join(", ");
      }
      // Handle date values
      if (
        key.includes("date") ||
        key.includes("start") ||
        key.includes("end")
      ) {
        // Filter dates are calendar dates: formatted without a time-zone shift.
        return formatDateOnly(value, "MM/dd/yyyy", value);
      }
      return value;
    }
    return String(value);
  };

  // Helper function to get human-readable filter names
  const getFilterLabel = (key: string): string => {
    const labels: Record<string, string> = {
      patient_name: "Name",
      patient_phone: "Phone",
      patient_gender: "Gender",
      patient_age_min: "Min Age",
      patient_age_max: "Max Age",
      include_tags: "Include Tags",
      exclude_tags: "Exclude Tags",
      call_statuses: "Call Statuses",
      call_types: "Call Types",
      call_date_start: "Call Date From",
      call_date_end: "Call Date To",
      has_calls: "Has Calls",
      latest_call_status: "Latest Call Status",
      call_attempts_min: "Min Call Attempts",
      call_attempts_max: "Max Call Attempts",
      never_answered: "Never Answered",
      consent_status: "Consent Status",
      consent_method: "Consent Method",
      needs_consent: "Needs Consent",
      last_contact_start: "Last Contact From",
      last_contact_end: "Last Contact To",
      due_for_contact: "Due for Contact",
      overdue_contact: "Overdue Contact",
      care_plan_status: "Care Plan Status",
      has_active_care_plan: "Has Active Care Plan",
      scheduled_calls_pending: "Scheduled Calls Pending",
      call_success_rate_min: "Min Success Rate",
      call_success_rate_max: "Max Success Rate",
      response_pattern: "Response Pattern",
      priority_level: "Priority Level",
      no_calls_start: "No Calls After",
      no_calls_end: "No Calls Before",
      no_call_types: "Exclude Call Types",
      no_call_types_start: "No Call Types After",
      no_call_types_end: "No Call Types Before",
    };
    return (
      labels[key] ||
      key.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase())
    );
  };

  // Group filters by category
  const groupedFilters = React.useMemo(() => {
    if (!filters) return {};

    const groups: Record<string, Array<{ key: string; value: any }>> = {
      "Patient Demographics": [],
      "Call History": [],
      Consent: [],
      "Time-based": [],
      "Care Management": [],
      Other: [],
    };

    Object.entries(filters).forEach(([key, value]) => {
      if (value === undefined || value === null || value === "") return;

      const filterData = { key, value };

      if (
        [
          "patient_name",
          "patient_phone",
          "patient_gender",
          "patient_age_min",
          "patient_age_max",
          "include_tags",
          "exclude_tags",
        ].includes(key)
      ) {
        groups["Patient Demographics"].push(filterData);
      } else if (
        [
          "call_statuses",
          "call_types",
          "call_date_start",
          "call_date_end",
          "has_calls",
          "latest_call_status",
          "call_attempts_min",
          "call_attempts_max",
          "never_answered",
          "call_success_rate_min",
          "call_success_rate_max",
          "response_pattern",
          "no_calls_start",
          "no_calls_end",
          "no_call_types",
          "no_call_types_start",
          "no_call_types_end",
        ].includes(key)
      ) {
        groups["Call History"].push(filterData);
      } else if (
        ["consent_status", "consent_method", "needs_consent"].includes(key)
      ) {
        groups["Consent"].push(filterData);
      } else if (
        [
          "last_contact_start",
          "last_contact_end",
          "due_for_contact",
          "overdue_contact",
        ].includes(key)
      ) {
        groups["Time-based"].push(filterData);
      } else if (
        [
          "care_plan_status",
          "has_active_care_plan",
          "scheduled_calls_pending",
        ].includes(key)
      ) {
        groups["Care Management"].push(filterData);
      } else {
        groups["Other"].push(filterData);
      }
    });

    // Remove empty groups
    return Object.fromEntries(
      Object.entries(groups).filter(([_, items]) => items.length > 0)
    );
  }, [filters]);

  const activeFilterCount = filters
    ? Object.keys(filters).filter(
        (key) =>
          filters[key as keyof FilterResult] !== undefined &&
          filters[key as keyof FilterResult] !== null &&
          filters[key as keyof FilterResult] !== ""
      ).length
    : 0;

  // Determine the result count and type
  const getResultInfo = () => {
    if (patientStats) {
      return {
        count: patientStats.patient_count,
        type: "patients",
      };
    }
    if (callAggregations) {
      return {
        count: callAggregations.total_calls,
        type: "calls",
      };
    }
    return { count: 0, type: "results" };
  };

  const resultInfo = getResultInfo();

  if (!isVisible || !query) {
    return null;
  }

  return (
    <Card className="mb-4 border-info/30 bg-info/10">
      <CardContent className="p-1 pl-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex flex-wrap items-center gap-2">
            <div className="w-2 h-2 bg-info rounded-full"></div>
            <span className="text-xs font-medium text-info">
              Smart Search Active
            </span>
            <span className="text-xs text-info">"{query}"</span>
            <span className="text-xs text-info">
              ({resultInfo.count} {resultInfo.type} found)
            </span>

            {/* Filters Info Popover */}
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 px-2 text-info hover:text-info hover:bg-info/10"
                >
                  <Info className="h-3 w-3 mr-1" />
                  {activeFilterCount} filter{activeFilterCount !== 1 ? "s" : ""}
                </Button>
              </PopoverTrigger>
              <PopoverContent
                className="w-[calc(100vw-2rem)] sm:w-96 max-h-96 overflow-y-auto"
                align="start"
              >
                <div className="space-y-4">
                  <div className="flex items-center justify-between border-b pb-2">
                    <h4 className="font-medium text-sm">Applied Filters</h4>
                    <Badge variant="secondary" className="text-xs">
                      {activeFilterCount} active
                    </Badge>
                  </div>

                  {Object.entries(groupedFilters).map(
                    ([groupName, groupFilters]) => (
                      <div key={groupName} className="space-y-2">
                        <h5 className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                          {groupName}
                        </h5>
                        <div className="space-y-1">
                          {groupFilters.map(({ key, value }) => (
                            <div
                              key={key}
                              className="flex items-center justify-between py-1 px-2 bg-muted/50 rounded-sm"
                            >
                              <span className="text-xs font-medium">
                                {getFilterLabel(key)}:
                              </span>
                              <span className="text-xs text-muted-foreground">
                                {formatFilterValue(key, value)}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )
                  )}

                  {/* Patient Stats Summary */}
                  {patientStats && (
                    <div className="border-t pt-2 space-y-2">
                      <h5 className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                        Patient Summary
                      </h5>
                      <div className="grid grid-cols-2 gap-2">
                        <div className="flex flex-col items-center p-2 bg-muted/50 rounded-sm">
                          <span className="text-xs text-muted-foreground">
                            Patients
                          </span>
                          <span className="text-sm font-medium">
                            {patientStats.patient_count}
                          </span>
                        </div>
                        <div className="flex flex-col items-center p-2 bg-muted/50 rounded-sm">
                          <span className="text-xs text-muted-foreground">
                            Avg Success Rate
                          </span>
                          <span className="text-sm font-medium">
                            {(patientStats.avg_success_rate * 100).toFixed(1)}%
                          </span>
                        </div>
                        <div className="flex flex-col items-center p-2 bg-muted/50 rounded-sm">
                          <span className="text-xs text-muted-foreground">
                            Consent Pending
                          </span>
                          <span className="text-sm font-medium">
                            {patientStats.consent_pending_count}
                          </span>
                        </div>
                        <div className="flex flex-col items-center p-2 bg-muted/50 rounded-sm">
                          <span className="text-xs text-muted-foreground">
                            Overdue
                          </span>
                          <span className="text-sm font-medium">
                            {patientStats.overdue_count}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Call Aggregations Summary */}
                  {callAggregations && (
                    <div className="border-t pt-2 space-y-2">
                      <h5 className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                        Call Summary
                      </h5>
                      <div className="grid grid-cols-2 gap-2">
                        <div className="flex flex-col items-center p-2 bg-info/10 rounded-sm">
                          <span className="text-xs text-info">
                            Total Calls
                          </span>
                          <span className="text-sm font-medium text-info">
                            {callAggregations.total_calls}
                          </span>
                        </div>
                        <div className="flex flex-col items-center p-2 bg-success/10 rounded-sm">
                          <span className="text-xs text-success">
                            Avg Duration
                          </span>
                          <span className="text-sm font-medium text-success">
                            {Math.round(callAggregations.avg_duration_seconds)}s
                          </span>
                        </div>
                        <div className="flex flex-col items-center p-2 bg-accent rounded-sm">
                          <span className="text-xs text-primary">
                            Total Minutes
                          </span>
                          <span className="text-sm font-medium text-primary">
                            {Math.round(
                              callAggregations.total_duration_minutes
                            )}
                          </span>
                        </div>
                        <div className="flex flex-col items-center p-2 bg-warning/10 rounded-sm">
                          <span className="text-xs text-warning">
                            Completed
                          </span>
                          <span className="text-sm font-medium text-warning">
                            {callAggregations.by_status?.completed || 0}
                          </span>
                        </div>
                      </div>

                      {/* Additional Call Status Breakdown */}
                      {Object.keys(callAggregations.by_status || {}).length >
                        1 && (
                        <div className="space-y-2">
                          <h6 className="text-xs font-medium text-muted-foreground">
                            By Status
                          </h6>
                          <div className="space-y-1">
                            {Object.entries(
                              callAggregations.by_status || {}
                            ).map(([status, count]) => (
                              <div
                                key={status}
                                className="flex items-center justify-between py-1 px-2 bg-muted/30 rounded-sm"
                              >
                                <span className="text-xs capitalize">
                                  {status.replace(/_/g, " ")}
                                </span>
                                <span className="text-xs font-medium">
                                  {count}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Strategy Type Breakdown */}
                      {Object.keys(callAggregations.by_strategy_type || {})
                        .length > 0 && (
                        <div className="space-y-2">
                          <h6 className="text-xs font-medium text-muted-foreground">
                            By Strategy Type
                          </h6>
                          <div className="space-y-1">
                            {Object.entries(
                              callAggregations.by_strategy_type || {}
                            ).map(([strategy, count]) => (
                              <div
                                key={strategy}
                                className="flex items-center justify-between py-1 px-2 bg-muted/30 rounded-sm"
                              >
                                <span className="text-xs capitalize">
                                  {strategy.replace(/_/g, " ")}
                                </span>
                                <span className="text-xs font-medium">
                                  {count}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </PopoverContent>
            </Popover>
          </div>

          <Button
            variant="ghost"
            size="sm"
            onClick={onClear}
            className="text-info hover:text-info hover:bg-info/10"
          >
            <X className="h-3 w-3 mr-1" />
            Clear Smart Search
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default SmartSearchActiveBar;
